import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from '@themesberg/react-bootstrap';
import { useCookies } from 'react-cookie';
import { getAvailableGames, assignGame, assignMultipleGames } from '../../../../../controller/api/employee';

const GameAssignModal = ({ show, onHide, employee, onSuccess }) => {
  const [cookies] = useCookies(['lottmekong']);
  const [games, setGames] = useState([]);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load available games when modal opens
  useEffect(() => {
    if (show) {
      loadGames();
      setError('');
      setSuccess('');
      // Set current games as selected if exists
      if (employee?.currentGames && employee.currentGames.length > 0) {
        setSelectedGameIds(employee.currentGames.map(g => g.id));
      } else if (employee?.currentGame) {
        // Backward compatibility
        setSelectedGameIds([employee.currentGame.id]);
      } else {
        setSelectedGameIds([]);
      }
    }
  }, [show, employee]);

  const loadGames = async () => {
    setLoading(true);
    try {
      const result = await getAvailableGames(cookies.lottmekong);
      if (result && result.status === 'success') {
        setGames(result.data || []);
      } else {
        setError('Không thể tải danh sách games');
      }
    } catch (err) {
      setError('Lỗi khi tải danh sách games');
    }
    setLoading(false);
  };

  const handleAssign = async () => {
    if (!employee) return;

    setAssigning(true);
    setError('');
    setSuccess('');

    try {
      const result = await assignMultipleGames(cookies.lottmekong, employee.id, selectedGameIds);

      if (result && result.status === 'success') {
        setSuccess(result.message || 'Gán games thành công');
        setTimeout(() => {
          onSuccess && onSuccess();
        }, 1500);
      } else {
        setError(result?.message || 'Không thể gán games');
      }
    } catch (err) {
      setError('Lỗi khi gán games');
    }

    setAssigning(false);
  };

  const handleGameToggle = (gameId) => {
    setSelectedGameIds(prev => {
      if (prev.includes(gameId)) {
        return prev.filter(id => id !== gameId);
      } else {
        return [...prev, gameId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedGameIds.length === games.length) {
      setSelectedGameIds([]);
    } else {
      setSelectedGameIds(games.map(g => g.id));
    }
  };

  if (!employee) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Gán Game cho Nhân viên
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        <div className="mb-3">
          <h6>Thông tin nhân viên:</h6>
          <div className="p-3 bg-light rounded">
            <div><strong>Tên đăng nhập:</strong> {employee.name}</div>
            <div className="mt-2">
              <strong>Games hiện tại:</strong>{' '}
              {employee.currentGames && employee.currentGames.length > 0 ? (
                <div className="mt-1">
                  {employee.currentGames.map(game => (
                    <Badge key={game.id} bg="success" className="me-1 mb-1">
                      {game.name}
                    </Badge>
                  ))}
                </div>
              ) : employee.currentGame ? (
                <Badge bg="success">{employee.currentGame.name}</Badge>
              ) : (
                <Badge bg="secondary">Chưa gán game</Badge>
              )}
            </div>
          </div>
        </div>

        <Form.Group className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="mb-0">Chọn Games:</Form.Label>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleSelectAll}
              disabled={loading || assigning}
            >
              {selectedGameIds.length === games.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
              <div className="mt-2">Đang tải danh sách games...</div>
            </div>
          ) : (
            <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {games.map(game => (
                <Form.Check
                  key={game.id}
                  type="checkbox"
                  id={`game-${game.id}`}
                  label={
                    <div>
                      <strong>{game.name}</strong> ({game.gameType})
                      <br />
                      <small className="text-muted">{game.prizeValue}</small>
                    </div>
                  }
                  checked={selectedGameIds.includes(game.id)}
                  onChange={() => handleGameToggle(game.id)}
                  disabled={assigning}
                  className="mb-2"
                />
              ))}
              {games.length === 0 && (
                <div className="text-center text-muted py-2">
                  Không có game nào khả dụng
                </div>
              )}
            </div>
          )}

          <Form.Text className="text-muted">
            Nhân viên sẽ chỉ có thể in vé của các games được chọn. Bỏ chọn tất cả để không gán game nào.
          </Form.Text>

          {selectedGameIds.length > 0 && (
            <div className="mt-2">
              <small className="text-info">
                Đã chọn {selectedGameIds.length} game(s)
              </small>
            </div>
          )}
        </Form.Group>

        {selectedGameIds.length > 0 && (
          <Alert variant="info" className="mb-0">
            <strong>Lưu ý:</strong> Nhân viên sẽ chỉ có thể in vé của {selectedGameIds.length} game(s) được gán.
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={assigning}>
          Hủy
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAssign}
          disabled={loading || assigning}
        >
          {assigning ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Đang gán...
            </>
          ) : (
            selectedGameIds.length > 0 ? `Gán ${selectedGameIds.length} Game(s)` : 'Bỏ gán tất cả Games'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GameAssignModal;
